import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, Trash2, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { CriticReviewData } from './wine/wineFormatting';
import { ReferencePriceGrid, CriticReviewsList, CriticReviewModal } from './wine/WineMetrics';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

enum AlertStatus {
    NEW = 'NEW',
    DISMISSED = 'DISMISSED'
}

enum AlertTrigger {
    NEW_LISTING = 'NEW_LISTING',
    PRICE_DROP = 'PRICE_DROP',
    REVALUATION = 'REVALUATION',
    MANUAL_SCAN = 'MANUAL_SCAN'
}

interface WineAlert {
    id: number;
    ruleId: number;
    ruleName: string;
    listingId: string;
    status: AlertStatus;
    triggerType: AlertTrigger;
    snapshotPrice: number;
    snapshotMarketValue: number;
    snapshotWineSearcherLow: number;
    snapshotLastTransaction?: number;
    snapshotHighestBid?: number;
    snapshotDiscountVsMarket: number;
    snapshotCriticScore: number;
    snapshotDrinkingFromYear?: number;
    snapshotDrinkingToYear?: number;
    previousPrice?: number;
    // Prior valuation references — set only on a REVALUATION where that reference moved
    previousMarketValue?: number;
    previousWineSearcherLow?: number;
    previousLastTransaction?: number;
    previousHighestBid?: number;
    // Prior vintage dimensions — set only on a CRITIC_REVIEW / DRINKING_WINDOW change
    previousCriticScore?: number;
    previousDrinkingFromYear?: number;
    previousDrinkingToYear?: number;
    newCriticReview?: string;
    triggeredAt: string;
    reviewedAt?: string;
    reviewNotes?: string;
    // Wine details
    wineName: string;
    producer: string;
    region: string;
    vintage: number;
    maturity?: string;
    drinkingFromYear?: number;
    drinkingToYear?: number;
    masterProductId?: string;
    masterProductVintageId?: string;
    masterSkuId?: string;

    // External source link (BBR for BBX, otherwise the source's own site)
    sourceCode?: string;
    sourceUrl?: string;

    // False once the listing has been deactivated (sold/withdrawn) — the alert is kept as a record
    listingActive?: boolean;

    // SKU identity
    bottleSizeMl?: number;
    caseSize?: number;
    canonicalSkuCode?: string;
    lwin16?: string;

    // Current reference prices (as of last import)
    marketPrice?: number;
    marketPriceUpdatedAt?: string;
    lastTransaction?: number;
    lastTransactionUpdatedAt?: string;
    highestBid?: number;
    highestBidUpdatedAt?: string;
    lowestListing?: number;
    lowestListingUpdatedAt?: string;
    wineSearcherPrice?: number;
    wineSearcherPriceUpdatedAt?: string;

    // This listing's price + DB-computed comparison percentages
    pricePerCase?: number;
    availableCases?: number;
    priceToMarketPercentage?: number;
    priceToWineSearcherPercentage?: number;
    priceToLastTransactionPercentage?: number;
    highestBidToMarketPercentage?: number;
    priceToLowestListingPercentage?: number;

    // Critic reviews for the vintage
    criticReviews?: CriticReviewData[];
}

const Alerts: React.FC = () => {
    const navigate = useNavigate();
    // Jump to the Master Products view, pinned to this alert's exact vintage.
    const openVintage = (alert: WineAlert) => {
        if (alert.masterProductVintageId) {
            navigate(`/master-products?vintageId=${encodeURIComponent(alert.masterProductVintageId)}`);
        }
    };
    const [alerts, setAlerts] = useState<WineAlert[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<AlertStatus | 'ALL'>(AlertStatus.NEW);
    const [selectedAlert, setSelectedAlert] = useState<WineAlert | null>(null);
    const [counts, setCounts] = useState<Record<string, number>>({});
    const [skuStatus, setSkuStatus] = useState<Record<string, any>>({});
    const [selectedReview, setSelectedReview] = useState<CriticReviewData | null>(null);

    useEffect(() => {
        fetchAlerts();
        fetchCounts();
    }, [selectedStatus]);

    useEffect(() => {
        fetch(`${API_BASE_URL}/api/bbr-account/sku-status`)
            .then(res => res.ok ? res.json() : {})
            .then(data => setSkuStatus(data || {}))
            .catch(() => {});
    }, []);

    const fetchAlerts = async () => {
        setLoading(true);
        try {
            const url = selectedStatus === 'ALL'
                ? `${API_BASE_URL}/api/alerts?size=100`
                : `${API_BASE_URL}/api/alerts?status=${selectedStatus}&size=100`;

            const response = await fetch(url);
            if (!response.ok) throw new Error('Failed to fetch alerts');
            const data = await response.json();
            setAlerts(data.alerts || []);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchCounts = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/alerts/counts`);
            if (!response.ok) throw new Error('Failed to fetch counts');
            const data = await response.json();
            setCounts(data);
        } catch (err: any) {
            console.error('Failed to fetch counts:', err);
        }
    };

    const updateAlertStatus = async (alertId: number, status: AlertStatus, notes?: string) => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/alerts/${alertId}/status`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status, notes }),
            });
            if (!response.ok) throw new Error('Failed to update alert');
            await fetchAlerts();
            await fetchCounts();
            setSelectedAlert(null);
        } catch (err: any) {
            alert(`Error: ${err.message}`);
        }
    };

    const deleteAlert = async (alertId: number) => {
        if (!window.confirm('Delete this alert?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/api/alerts/${alertId}`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error('Failed to delete alert');
            await fetchAlerts();
            await fetchCounts();
        } catch (err: any) {
            alert(`Error: ${err.message}`);
        }
    };

    const formatTriggerType = (trigger: AlertTrigger) => {
        switch (trigger) {
            case AlertTrigger.NEW_LISTING: return 'New Listing';
            case AlertTrigger.PRICE_DROP: return 'Price Drop';
            case AlertTrigger.REVALUATION: return 'Revaluation';
            case AlertTrigger.MANUAL_SCAN: return 'Manual Scan';
        }
    };

    const formatDiscount = (discount: number) => {
        return discount ? `${(discount * 100).toFixed(0)}%` : 'N/A';
    };

    const formatMaturity = (m?: string) => {
        if (!m || m === 'UNKNOWN') return null;
        return m.toLowerCase().split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    };

    const styles = {
        container: {
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '24px',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
        },
        title: {
            fontSize: '24px',
            fontWeight: '600',
            color: 'var(--ink-900)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
        },
        filterBar: {
            display: 'flex',
            gap: '8px',
            marginBottom: '24px',
            flexWrap: 'wrap' as const,
        },
        filterButton: (active: boolean) => ({
            padding: '8px 16px',
            backgroundColor: active ? 'var(--claret-600)' : 'white',
            color: active ? 'white' : 'var(--ink-500)',
            border: `1px solid ${active ? 'var(--claret-600)' : 'var(--border)'}`,
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        }),
        badge: {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
        },
        alertCard: {
            backgroundColor: 'white',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
            cursor: 'pointer',
            transition: 'all 0.2s',
        },
        alertCardHover: {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
        alertHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '12px',
        },
        wineInfo: {
            flex: 1,
        },
        wineName: {
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--ink-900)',
            marginBottom: '4px',
        },
        wineDetails: {
            fontSize: '14px',
            color: 'var(--ink-500)',
            marginBottom: '12px',
        },
        priceInfo: {
            display: 'flex',
            flexWrap: 'wrap' as const,
            gap: '24px',
            marginBottom: '12px',
        },
        priceItem: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '4px',
        },
        priceLabel: {
            fontSize: '12px',
            color: 'var(--ink-500)',
            textTransform: 'uppercase' as const,
        },
        priceValue: {
            fontSize: '16px',
            fontWeight: '600',
            color: 'var(--ink-900)',
        },
        discountBadge: (discount: number) => ({
            display: 'inline-flex',
            padding: '4px 8px',
            backgroundColor: discount > 0.2 ? 'var(--success-bg)' : discount > 0.1 ? 'var(--warning-bg)' : 'var(--danger-bg)',
            color: discount > 0.2 ? 'var(--success)' : discount > 0.1 ? '#854d0e' : 'var(--danger)',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '600',
        }),
        alertMeta: {
            display: 'flex',
            gap: '16px',
            fontSize: '13px',
            color: 'var(--ink-500)',
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid var(--border)',
        },
        actions: {
            display: 'flex',
            gap: '8px',
            marginTop: '12px',
        },
        actionButton: (variant: 'primary' | 'secondary' | 'danger' = 'secondary') => ({
            padding: '8px 16px',
            backgroundColor: variant === 'primary' ? 'var(--claret-600)' : variant === 'danger' ? 'var(--danger)' : 'white',
            color: variant === 'primary' ? 'white' : variant === 'danger' ? 'white' : 'var(--ink-500)',
            border: `1px solid ${variant === 'primary' ? 'var(--claret-600)' : variant === 'danger' ? 'var(--danger)' : 'var(--border)'}`,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
        }),
        statusBadge: (status: AlertStatus) => {
            const colors = {
                NEW: { bg: 'var(--info-bg)', color: 'var(--info)' },
                DISMISSED: { bg: 'var(--danger-bg)', color: 'var(--danger)' },
            };
            return {
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 12px',
                backgroundColor: colors[status].bg,
                color: colors[status].color,
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
            };
        },
        triggerBadge: {
            display: 'inline-flex',
            padding: '4px 8px',
            backgroundColor: 'var(--border)',
            color: 'var(--ink-600)',
            borderRadius: '4px',
            fontSize: '12px',
        },
        emptyState: {
            textAlign: 'center' as const,
            padding: '48px',
            color: 'var(--ink-500)',
        },
        detailBar: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap' as const,
            marginTop: '4px',
        },
        skuChip: {
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 10px',
            backgroundColor: 'var(--border)',
            color: 'var(--ink-700)',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '600' as const,
            fontFamily: 'monospace',
        },
        linkButton: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            backgroundColor: 'var(--info)',
            color: 'white',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '600' as const,
            textDecoration: 'none',
        },
        detailPanel: {
            marginTop: '12px',
            padding: '16px',
            backgroundColor: 'var(--canvas)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
        },
        detailSectionLabel: {
            fontSize: '12px',
            color: 'var(--ink-500)',
            fontWeight: '600' as const,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px',
            marginBottom: '4px',
        },
    };

    if (loading && alerts.length === 0) {
        return (
            <div style={{...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh'}}>
                <Loader2 size={32} className="spinner" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh'}}>
                <div style={{textAlign: 'center'}}>
                    <AlertCircle size={48} color="var(--danger)" />
                    <p style={{marginTop: '16px', color: 'var(--danger)'}}>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>
                    <Bell size={28} />
                    Wine Alerts
                </h1>
            </div>

            <div style={styles.filterBar}>
                <button
                    style={styles.filterButton(selectedStatus === AlertStatus.NEW)}
                    onClick={() => setSelectedStatus(AlertStatus.NEW)}
                >
                    New
                    {counts.NEW > 0 && <span style={styles.badge}>{counts.NEW}</span>}
                </button>
                <button
                    style={styles.filterButton(selectedStatus === AlertStatus.DISMISSED)}
                    onClick={() => setSelectedStatus(AlertStatus.DISMISSED)}
                >
                    Dismissed
                    {counts.DISMISSED > 0 && <span style={styles.badge}>{counts.DISMISSED}</span>}
                </button>
                <button
                    style={styles.filterButton(selectedStatus === 'ALL')}
                    onClick={() => setSelectedStatus('ALL')}
                >
                    All
                </button>
            </div>

            {alerts.length === 0 ? (
                <div style={styles.emptyState}>
                    <p>No alerts found</p>
                    <p style={{fontSize: '14px', marginTop: '8px'}}>
                        {selectedStatus === AlertStatus.NEW
                            ? 'Create alert rules to start getting notified about interesting wines!'
                            : `No ${selectedStatus.toLowerCase()} alerts`}
                    </p>
                </div>
            ) : (
                alerts.map((alert) => (
                    <div key={alert.id} style={styles.alertCard}>
                        <div style={styles.alertHeader}>
                            <div style={styles.wineInfo}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px'}}>
                                    {alert.masterProductVintageId ? (
                                        <h3
                                            style={{ ...styles.wineName, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                            title="View this vintage in Master Products"
                                            onClick={() => openVintage(alert)}
                                            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--claret-600)'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.color = ''; }}
                                        >
                                            {alert.wineName}
                                            <ExternalLink size={15} style={{ opacity: 0.6 }} />
                                        </h3>
                                    ) : (
                                        <h3 style={styles.wineName}>{alert.wineName}</h3>
                                    )}
                                    <span style={styles.statusBadge(alert.status)}>{alert.status}</span>
                                    <span style={styles.triggerBadge}>{formatTriggerType(alert.triggerType)}</span>
                                    {alert.listingActive === false && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 12px', backgroundColor: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                                            Listing gone
                                        </span>
                                    )}
                                    {alert.masterSkuId && skuStatus[alert.masterSkuId] && (() => {
                                        const ss = skuStatus[alert.masterSkuId];
                                        return (
                                            <>
                                                {ss.inCellar && (() => {
                                                    const count = ss.totalCases ?? ss.cellarItemCount;
                                                    const holdings = Array.isArray(ss.holdings) ? ss.holdings : [];
                                                    let priceText = '';
                                                    if (holdings.length > 1) {
                                                        // List each holding so different purchase prices stay visible.
                                                        priceText = ' · ' + holdings.map((h: any) =>
                                                            `${h.cases} ${h.cases === 1 ? 'case' : 'cases'} £${h.purchasePrice != null ? Number(h.purchasePrice).toFixed(0) : '?'}`
                                                        ).join(' · ');
                                                    } else if (ss.purchasePrice != null) {
                                                        priceText = ` · Paid £${Number(ss.purchasePrice).toFixed(0)}`;
                                                    }
                                                    return (
                                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 12px', backgroundColor: 'var(--info-bg)', color: 'var(--info)', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                                                            In Cellar ({count} {count === 1 ? 'case' : 'cases'}){priceText}
                                                        </span>
                                                    );
                                                })()}
                                                {ss.hasBidsReceived && (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 12px', backgroundColor: 'var(--warning-bg)', color: '#854d0e', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                                                        Bid £{ss.highestBidReceived != null ? Number(ss.highestBidReceived).toFixed(0) : '?'}
                                                    </span>
                                                )}
                                                {ss.hasBidsPlaced && (
                                                    // Purple = bids-placed, matching the My Account block on Master Products.
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 12px', backgroundColor: '#f3e8ff', color: '#5b21b6', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                                                        My Bid {ss.lowestBidPlaced != null ? `£${Number(ss.lowestBidPlaced).toFixed(0)}` : 'placed'}
                                                        {ss.bidsPlacedCount > 1 ? ` (${ss.bidsPlacedCount} bids)` : ''}
                                                    </span>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                                <div style={styles.wineDetails}>
                                    {alert.producer} • {alert.region} • {alert.vintage}
                                    {alert.snapshotCriticScore && ` • ${alert.snapshotCriticScore} pts`}
                                    {formatMaturity(alert.maturity) && ` • ${formatMaturity(alert.maturity)}`}
                                    {(alert.drinkingFromYear || alert.drinkingToYear) && ` • Drink ${alert.drinkingFromYear || '?'}–${alert.drinkingToYear || '?'}`}
                                </div>
                                <div style={{fontSize: '13px', color: 'var(--ink-400)', marginBottom: '12px'}}>
                                    Rule: <strong>{alert.ruleName}</strong>
                                </div>

                                {/* Full reference set, shown identically for every trigger/rule so the
                                    layout is consistent regardless of which rule fired the alert. */}
                                <div style={styles.detailSectionLabel}>At trigger</div>
                                <div style={styles.priceInfo}>
                                    <div style={styles.priceItem}>
                                        <span style={styles.priceLabel}>Listing Price</span>
                                        <span style={styles.priceValue}>£{alert.snapshotPrice?.toFixed(2)}</span>
                                    </div>
                                    {([
                                        { label: 'Market Value', v: alert.snapshotMarketValue },
                                        { label: 'Wine Searcher', v: alert.snapshotWineSearcherLow },
                                        { label: 'Last Transaction', v: alert.snapshotLastTransaction },
                                        { label: 'Highest Bid', v: alert.snapshotHighestBid },
                                    ] as Array<{ label: string; v?: number }>).map(r => (
                                        <div style={styles.priceItem} key={r.label}>
                                            <span style={styles.priceLabel}>{r.label}</span>
                                            <span style={styles.priceValue}>{r.v != null ? `£${r.v.toFixed(2)}` : 'N/A'}</span>
                                        </div>
                                    ))}
                                    <div style={styles.priceItem}>
                                        <span style={styles.priceLabel}>Discount vs Market</span>
                                        <span style={styles.discountBadge(alert.snapshotDiscountVsMarket || 0)}>
                                            {formatDiscount(alert.snapshotDiscountVsMarket)}
                                        </span>
                                    </div>
                                    {alert.previousPrice != null && alert.previousPrice !== alert.snapshotPrice && (
                                        <div style={styles.priceItem}>
                                            <span style={styles.priceLabel}>Previous Price</span>
                                            <span style={styles.priceValue}>£{alert.previousPrice.toFixed(2)}</span>
                                        </div>
                                    )}
                                </div>

                                {/* What changed — the value(s) that moved and fired this alert
                                    (valuation references, critic score, or drinking window) */}
                                {(() => {
                                    const money = (n: number) => `£${n.toFixed(2)}`;
                                    const year = (n: number) => `${n}`;
                                    const changes: Array<{ label: string; from?: number; to?: number; fmt: (n: number) => string }> = [
                                        { label: 'Market Value', from: alert.previousMarketValue, to: alert.snapshotMarketValue, fmt: money },
                                        { label: 'Wine Searcher', from: alert.previousWineSearcherLow, to: alert.snapshotWineSearcherLow, fmt: money },
                                        { label: 'Last Transaction', from: alert.previousLastTransaction, to: alert.snapshotLastTransaction, fmt: money },
                                        { label: 'Highest Bid', from: alert.previousHighestBid, to: alert.snapshotHighestBid, fmt: money },
                                        { label: 'Critic Score', from: alert.previousCriticScore, to: alert.snapshotCriticScore, fmt: year },
                                        { label: 'Drink From', from: alert.previousDrinkingFromYear, to: alert.snapshotDrinkingFromYear, fmt: year },
                                        { label: 'Drink To', from: alert.previousDrinkingToYear, to: alert.snapshotDrinkingToYear, fmt: year },
                                    ].filter(c => c.from != null && c.to != null && c.from !== c.to);
                                    // A new critic review is surfaced even when it didn't move the top score.
                                    const newReview = alert.newCriticReview;
                                    if (changes.length === 0 && !newReview) return null;
                                    return (
                                        <>
                                            <div style={styles.detailSectionLabel}>What changed</div>
                                            <div style={styles.priceInfo}>
                                                {changes.map(c => (
                                                    <div style={styles.priceItem} key={c.label}>
                                                        <span style={styles.priceLabel}>{c.label}</span>
                                                        <span style={styles.priceValue}>{c.fmt(c.from!)} → {c.fmt(c.to!)}</span>
                                                    </div>
                                                ))}
                                                {newReview && (
                                                    <div style={styles.priceItem}>
                                                        <span style={styles.priceLabel}>New Review</span>
                                                        <span style={styles.priceValue}>{newReview}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    );
                                })()}

                                {/* SKU + BBR link + headline vs-market + expand toggle */}
                                <div style={styles.detailBar}>
                                    {(alert.bottleSizeMl && alert.caseSize) ? (
                                        <span style={styles.skuChip}>{alert.bottleSizeMl}ml × {alert.caseSize} case</span>
                                    ) : alert.canonicalSkuCode ? (
                                        <span style={styles.skuChip}>{alert.canonicalSkuCode}</span>
                                    ) : null}

                                    {alert.sourceUrl && (
                                        <a
                                            href={alert.sourceUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={styles.linkButton}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <ExternalLink size={14} />
                                            {alert.sourceCode === 'BBX' ? 'View on BBR' : 'View source'}
                                        </a>
                                    )}

                                </div>

                                <div style={styles.detailPanel}>
                                        <div style={styles.detailSectionLabel}>Current · reference prices &amp; this listing vs each (last import)</div>
                                        <ReferencePriceGrid
                                            align="left"
                                            style={{ minWidth: 'auto' }}
                                            rows={[
                                                { label: 'Market Price', price: alert.marketPrice, date: alert.marketPriceUpdatedAt, comparison: alert.priceToMarketPercentage },
                                                { label: 'Last Transaction', price: alert.lastTransaction, date: alert.lastTransactionUpdatedAt, comparison: alert.priceToLastTransactionPercentage },
                                                { label: 'Highest Bid', price: alert.highestBid, date: alert.highestBidUpdatedAt },
                                                { label: 'Lowest Listing', price: alert.lowestListing, date: alert.lowestListingUpdatedAt, comparison: alert.priceToLowestListingPercentage },
                                                { label: 'Wine Searcher', price: alert.wineSearcherPrice, date: alert.wineSearcherPriceUpdatedAt, comparison: alert.priceToWineSearcherPercentage },
                                            ]}
                                        />
                                        {alert.criticReviews && alert.criticReviews.length > 0 && (
                                            <div style={{ marginTop: '16px' }}>
                                                <div style={styles.detailSectionLabel}>Critic Reviews</div>
                                                <CriticReviewsList reviews={alert.criticReviews} onSelect={setSelectedReview} />
                                            </div>
                                        )}
                                    </div>

                                {alert.status === AlertStatus.NEW && (
                                    <div style={styles.actions}>
                                        <button
                                            style={styles.actionButton('secondary')}
                                            onClick={() => updateAlertStatus(alert.id, AlertStatus.DISMISSED)}
                                        >
                                            <X size={16} />
                                            Dismiss
                                        </button>
                                    </div>
                                )}

                                <div style={styles.alertMeta}>
                                    <span>Triggered: {new Date(alert.triggeredAt).toLocaleString()}</span>
                                    <button
                                        style={{...styles.actionButton('danger'), marginLeft: 'auto', padding: '4px 8px'}}
                                        onClick={() => deleteAlert(alert.id)}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))
            )}

            <CriticReviewModal review={selectedReview} onClose={() => setSelectedReview(null)} />
        </div>
    );
};

export default Alerts;
